import {
    createRootRouteWithContext,
    createRoute,
    createRouter,
    Outlet,
    redirect,
} from "@tanstack/react-router";
import type { Runtime } from "./runtime/buildRuntime";
import { LoginPage } from "./pages/LoginPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { WorkspaceEntry } from "./pages/WorkspaceEntry";
import { NotFoundPage } from "./pages/NotFoundPage";
import { RouteErrorPage } from "./pages/RouteErrorPage";
import { WorkspaceManager } from "../managers/WorkspaceManager";

export interface RouterContext {
    runtime: Runtime;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
    component: () => <Outlet />,
});

const requireAuth = ({ context, location }: { context: RouterContext; location: { href: string } }) => {
    if (!context.runtime.session.getAccessToken()) {
        // Preserve where the user was headed so login can return them there.
        throw redirect({ to: "/auth/login", search: { redirect: location.href } } as unknown as Parameters<typeof redirect>[0]);
    }
};

const indexRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/",
    beforeLoad: () => {
        throw redirect({ to: "/wc" });
    },
});

const loginRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/auth/login",
    component: LoginPage,
});

const forgotRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/auth/forgot-password",
    component: ForgotPasswordPage,
});

// /wc → resolver that redirects to /wc/{orgCode}/{firstAction}/list
const workspaceEntryRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/wc",
    beforeLoad: requireAuth,
    component: WorkspaceEntry,
});

// /wc/{orgCode}/{...drill chain} — the workspace splat (routing.md)
const workspaceRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/wc/$orgCode/$",
    beforeLoad: requireAuth,
    component: WorkspaceManager,
});

const routeTree = rootRoute.addChildren([
    indexRoute,
    loginRoute,
    forgotRoute,
    workspaceEntryRoute,
    workspaceRoute,
]);

export function createAppRouter(runtime: Runtime) {
    return createRouter({
        routeTree,
        context: { runtime },
        defaultNotFoundComponent: NotFoundPage,
        defaultErrorComponent: RouteErrorPage,
    });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module "@tanstack/react-router" {
    interface Register {
        router: AppRouter;
    }
}
